/**
 * TotpMfaPanel — v4.6.3
 *
 * Authenticator-App (TOTP) MFA management panel for the User Dashboard.
 * Coexists with the existing Email-PIN 2FA toggle: TOTP, when enabled,
 * replaces the email-PIN MFA challenge at login (per backend precedence in
 * api/initiate-login.js v4.3.91).
 *
 * All sensitive operations (Enable / Disable / Regenerate backup codes)
 * are gated by the email-PIN action-verification flow (v4.6.0+) via
 * PinVerificationModal (purpose='security_change'). Plaintext secrets and
 * backup codes are shown ONCE — never persisted client-side beyond the
 * setup/regen wizard scope.
 */
import React, { useEffect, useState } from 'react';
import { ensureSessionHeaders } from '../lib/serverSession';
import { PinVerificationModal } from './PinVerificationModal';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface TotpMfaPanelProps {
  userId: number;
  email: string;
}

interface MfaStatus {
  isEnabled: boolean;
  setupComplete: boolean;
  backupCodesRemaining: number;
  enabledAt?: string | null;
  lastUsedAt?: string | null;
  lockedUntil?: string | null;
}

type Stage =
  | 'idle'
  | 'awaiting-enable-token'    // PinVerificationModal open for setup re-auth
  | 'enable-wizard'            // QR + secret + 6-digit input
  | 'show-backup-codes'        // post-enable backup codes (one-time)
  | 'awaiting-disable-token'   // PinVerificationModal open for disable re-auth
  | 'disable-confirm'          // ask for TOTP/backup code
  | 'awaiting-regen-token'     // PinVerificationModal open for regen re-auth
  | 'regen-confirm'            // ask for TOTP code
  | 'show-new-backup-codes';   // post-regen new backup codes (one-time)

export const TotpMfaPanel: React.FC<TotpMfaPanelProps> = ({ userId, email }) => {
  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('idle');

  // Wizard state (cleared on reset).
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [_otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verifyCode, setVerifyCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  // Non-secret action_verification_tokens.id returned by the magic-link flow
  // (the secret 256-bit token never reaches the browser).
  const [verificationId, setVerificationId] = useState<number | null>(null);

  const reset = () => {
    setStage('idle');
    setQrDataUrl(null);
    setOtpauthUrl(null);
    setSecret(null);
    setBackupCodes([]);
    setVerifyCode('');
    setConfirmCode('');
    setVerificationId(null);
    setError(null);
    setBusy(false);
  };

  const loadStatus = async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/api/auth/mfa/status`, {
        method: 'GET',
        headers: { ...await ensureSessionHeaders() },
      });
      const j = await r.json();
      if (j.success) setStatus({
        isEnabled: !!j.isEnabled,
        setupComplete: !!j.setupComplete,
        backupCodesRemaining: j.backupCodesRemaining || 0,
        enabledAt: j.enabledAt,
        lastUsedAt: j.lastUsedAt,
        lockedUntil: j.lockedUntil,
      });
    } catch (e) {
      console.error('[TotpMfaPanel] status load failed:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [userId]);

  // ---------- Enable flow ----------
  const onEnableTokenVerified = async (vid?: number) => {
    if (!vid || !Number.isFinite(vid)) { setError('Verification id missing.'); reset(); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/auth/mfa/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ verificationId: vid }),
      });
      const j = await r.json();
      if (!j.success) { setError(j.error || 'Failed to start setup.'); setStage('idle'); return; }
      setQrDataUrl(j.qrCodeDataUrl || null);
      setOtpauthUrl(j.otpauthUrl || null);
      setSecret(j.secret || null);
      setBackupCodes(Array.isArray(j.backupCodes) ? j.backupCodes : []);
      setStage('enable-wizard');
    } catch (e: any) {
      setError(e?.message || 'Network error.');
      setStage('idle');
    } finally { setBusy(false); }
  };

  const onVerifySetup = async () => {
    if (!/^\d{6}$/.test(verifyCode.trim())) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/auth/mfa/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ token: verifyCode.trim() }),
      });
      const j = await r.json();
      if (!j.success) { setError(j.error || 'Invalid code.'); return; }
      setInfo('Authenticator app enabled. Save the backup codes below — they will not be shown again.');
      setSecret(null);
      setQrDataUrl(null);
      setVerifyCode('');
      setStage('show-backup-codes');
      await loadStatus();
    } catch (e: any) {
      setError(e?.message || 'Network error.');
    } finally { setBusy(false); }
  };

  // ---------- Disable flow ----------
  const onDisableTokenVerified = async (vid?: number) => {
    if (!vid || !Number.isFinite(vid)) { setError('Verification id missing.'); reset(); return; }
    setVerificationId(vid);
    setStage('disable-confirm');
  };

  const onDisableSubmit = async () => {
    const code = confirmCode.trim();
    if (!/^\d{6}$/.test(code) && !/^\d{8}$/.test(code)) {
      setError('Enter your 6-digit authenticator code or 8-digit backup code.');
      return;
    }
    if (!verificationId) { setError('Verification id missing.'); reset(); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/auth/mfa/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ verificationId, code }),
      });
      const j = await r.json();
      if (!j.success) { setError(j.error || 'Failed to disable MFA.'); return; }
      setInfo('Authenticator app disabled. All trusted devices have been revoked.');
      reset();
      await loadStatus();
    } catch (e: any) {
      setError(e?.message || 'Network error.');
    } finally { setBusy(false); }
  };

  // ---------- Regenerate backup codes ----------
  const onRegenTokenVerified = async (vid?: number) => {
    if (!vid || !Number.isFinite(vid)) { setError('Verification id missing.'); reset(); return; }
    setVerificationId(vid);
    setStage('regen-confirm');
  };

  const onRegenSubmit = async () => {
    const code = confirmCode.trim();
    if (!/^\d{6}$/.test(code)) { setError('Enter your current 6-digit authenticator code.'); return; }
    if (!verificationId) { setError('Verification id missing.'); reset(); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/auth/mfa/regenerate-backup-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...await ensureSessionHeaders() },
        body: JSON.stringify({ verificationId, code }),
      });
      const j = await r.json();
      if (!j.success) { setError(j.error || 'Failed to regenerate backup codes.'); return; }
      setBackupCodes(Array.isArray(j.backupCodes) ? j.backupCodes : []);
      setInfo('New backup codes generated. Your previous codes are now invalid.');
      setConfirmCode('');
      setVerificationId(null);
      setStage('show-new-backup-codes');
      await loadStatus();
    } catch (e: any) {
      setError(e?.message || 'Network error.');
    } finally { setBusy(false); }
  };

  // ---------- UI helpers ----------
  const copyToClipboard = (text: string) => {
    try { navigator.clipboard?.writeText(text); } catch { /* ignore */ }
  };
  const downloadBackupCodes = (codes: string[]) => {
    const blob = new Blob(
      [`Aureus Africa — TOTP Backup Codes\nAccount: ${email}\nGenerated: ${new Date().toISOString()}\n\nEach code can be used ONCE. Store them somewhere safe.\n\n${codes.join('\n')}\n`],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `aureus-backup-codes-${Date.now()}.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ---------- Render ----------
  if (loading) {
    return (
      <div className="border-b border-gray-600 pb-4">
        <h4 className="text-white font-medium mb-2">Authenticator App (TOTP)</h4>
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  const enabled = !!status?.isEnabled;

  return (
    <div className="border-b border-gray-600 pb-4">
      <h4 className="text-white font-medium mb-2">Authenticator App (TOTP)</h4>
      <p className="text-white text-sm mb-3">
        Use Google Authenticator, Authy, 1Password, or any RFC&nbsp;6238 app for the strongest login protection.
        When enabled, TOTP replaces the email-PIN code at login.
      </p>

      {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
      {info && <p className="text-green-400 text-sm mb-2">{info}</p>}

      {/* Idle: show status + primary action */}
      {stage === 'idle' && (
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white text-sm">
              {enabled ? 'Enabled' : 'Not enabled'}
              {enabled && status?.backupCodesRemaining != null && (
                <span className="text-gray-400"> · {status.backupCodesRemaining} backup codes remaining</span>
              )}
            </p>
            {enabled && status?.lastUsedAt && (
              <p className="text-gray-400 text-xs">Last used: {new Date(status.lastUsedAt).toLocaleString()}</p>
            )}
            {!enabled && (
              <p className="text-gray-400 text-xs">Recommended for accounts holding shares.</p>
            )}
          </div>
          <div className="flex gap-2">
            {!enabled && (
              <button
                type="button"
                onClick={() => { setError(null); setInfo(null); setStage('awaiting-enable-token'); }}
                disabled={busy}
                className="px-3 py-1.5 text-sm rounded bg-yellow-600 hover:bg-yellow-700 text-white disabled:opacity-50"
              >Enable</button>
            )}
            {enabled && (
              <>
                <button
                  type="button"
                  onClick={() => { setError(null); setInfo(null); setStage('awaiting-regen-token'); }}
                  disabled={busy}
                  className="px-3 py-1.5 text-sm rounded bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-50"
                >Regenerate codes</button>
                <button
                  type="button"
                  onClick={() => { setError(null); setInfo(null); setStage('awaiting-disable-token'); }}
                  disabled={busy}
                  className="px-3 py-1.5 text-sm rounded bg-red-700 hover:bg-red-800 text-white disabled:opacity-50"
                >Disable</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Enable wizard: scan QR + verify 6-digit code */}
      {stage === 'enable-wizard' && (
        <div className="mt-3 p-4 bg-gray-900/60 rounded border border-gray-700">
          <p className="text-white text-sm mb-3">
            1. Scan this QR code with your authenticator app (or enter the secret manually).
            <br />
            2. Enter the 6-digit code shown in the app to confirm.
          </p>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="TOTP QR Code" className="bg-white p-2 rounded mb-3" width={200} height={200} />
          ) : (
            <p className="text-gray-400 text-xs mb-3">QR rendering unavailable — use the secret below.</p>
          )}
          {secret && (
            <div className="mb-3">
              <p className="text-gray-400 text-xs">Secret (manual entry):</p>
              <div className="flex items-center gap-2">
                <code className="text-yellow-400 text-sm break-all">{secret}</code>
                <button type="button" onClick={() => copyToClipboard(secret!)} className="text-xs text-gray-300 hover:text-white">Copy</button>
              </div>
            </div>
          )}
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
            placeholder="6-digit code"
            className="w-full sm:w-48 px-3 py-2 bg-gray-800 text-white rounded border border-gray-600 mb-3"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onVerifySetup}
              disabled={busy || verifyCode.length !== 6}
              className="px-3 py-1.5 text-sm rounded bg-yellow-600 hover:bg-yellow-700 text-white disabled:opacity-50"
            >{busy ? 'Verifying…' : 'Verify & Enable'}</button>
            <button type="button" onClick={reset} disabled={busy} className="px-3 py-1.5 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white">Cancel</button>
          </div>
        </div>
      )}

      {/* Show backup codes (post-enable or post-regen) */}
      {(stage === 'show-backup-codes' || stage === 'show-new-backup-codes') && backupCodes.length > 0 && (
        <div className="mt-3 p-4 bg-gray-900/60 rounded border border-yellow-700">
          <p className="text-yellow-300 text-sm font-medium mb-2">
            Save these {backupCodes.length} backup codes — they will not be shown again.
          </p>
          <p className="text-gray-300 text-xs mb-3">
            Each code can be used ONCE if you lose access to your authenticator app.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
            {backupCodes.map((c) => (
              <code key={c} className="text-yellow-400 text-sm bg-gray-800 px-2 py-1 rounded text-center">{c}</code>
            ))}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => copyToClipboard(backupCodes.join('\n'))} className="px-3 py-1.5 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white">Copy all</button>
            <button type="button" onClick={() => downloadBackupCodes(backupCodes)} className="px-3 py-1.5 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white">Download .txt</button>
            <button type="button" onClick={reset} className="px-3 py-1.5 text-sm rounded bg-yellow-600 hover:bg-yellow-700 text-white">I&rsquo;ve saved them</button>
          </div>
        </div>
      )}

      {/* Disable confirm: ask for TOTP/backup code (action token already verified) */}
      {stage === 'disable-confirm' && (
        <div className="mt-3 p-4 bg-gray-900/60 rounded border border-red-700">
          <p className="text-white text-sm mb-3">
            Enter your current 6-digit authenticator code <em>or</em> an 8-digit backup code to disable MFA.
            All trusted devices will be revoked.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={8}
            value={confirmCode}
            onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ''))}
            placeholder="6 or 8 digits"
            className="w-full sm:w-48 px-3 py-2 bg-gray-800 text-white rounded border border-gray-600 mb-3"
          />
          <div className="flex gap-2">
            <button type="button" onClick={onDisableSubmit} disabled={busy || (confirmCode.length !== 6 && confirmCode.length !== 8)} className="px-3 py-1.5 text-sm rounded bg-red-700 hover:bg-red-800 text-white disabled:opacity-50">{busy ? 'Disabling…' : 'Disable MFA'}</button>
            <button type="button" onClick={reset} disabled={busy} className="px-3 py-1.5 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white">Cancel</button>
          </div>
        </div>
      )}

      {/* Regenerate confirm */}
      {stage === 'regen-confirm' && (
        <div className="mt-3 p-4 bg-gray-900/60 rounded border border-gray-700">
          <p className="text-white text-sm mb-3">
            Enter your current 6-digit authenticator code to issue a new set of backup codes.
            Your previous codes will be invalidated immediately.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={confirmCode}
            onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ''))}
            placeholder="6-digit code"
            className="w-full sm:w-48 px-3 py-2 bg-gray-800 text-white rounded border border-gray-600 mb-3"
          />
          <div className="flex gap-2">
            <button type="button" onClick={onRegenSubmit} disabled={busy || confirmCode.length !== 6} className="px-3 py-1.5 text-sm rounded bg-yellow-600 hover:bg-yellow-700 text-white disabled:opacity-50">{busy ? 'Generating…' : 'Generate new codes'}</button>
            <button type="button" onClick={reset} disabled={busy} className="px-3 py-1.5 text-sm rounded bg-gray-700 hover:bg-gray-600 text-white">Cancel</button>
          </div>
        </div>
      )}

      {/* Email-PIN re-auth modals (v4.6.0+) */}
      {stage === 'awaiting-enable-token' && (
        <PinVerificationModal
          isOpen={true}
          onClose={reset}
          onVerified={onEnableTokenVerified}
          userId={userId}
          email={email}
          purpose="security_change"
          title="Enable Authenticator App"
          description="Enter the 6-digit code sent to your email to confirm enabling TOTP MFA."
        />
      )}
      {stage === 'awaiting-disable-token' && (
        <PinVerificationModal
          isOpen={true}
          onClose={reset}
          onVerified={onDisableTokenVerified}
          userId={userId}
          email={email}
          purpose="security_change"
          title="Disable Authenticator App"
          description="Enter the 6-digit code sent to your email. After verification you will be asked for your authenticator code to finish disabling MFA."
        />
      )}
      {stage === 'awaiting-regen-token' && (
        <PinVerificationModal
          isOpen={true}
          onClose={reset}
          onVerified={onRegenTokenVerified}
          userId={userId}
          email={email}
          purpose="security_change"
          title="Regenerate Backup Codes"
          description="Enter the 6-digit code sent to your email to confirm regenerating your backup codes."
        />
      )}
    </div>
  );
};

export default TotpMfaPanel;
